import axios from 'axios';
import * as crm_urls from '../../urls/api-shipments/crm';
import { entitlementsMetrics } from '../../urls/api-shipments/entitlements';

// The global axios interceptor (configured in Admin.jsx) attaches the Auth0
// access token, so these calls are authenticated automatically. The backend
// enforces CRM access + role on every endpoint.
const unwrap = response => (response && response.data ? response.data.payload : null);

const crmClient = {
  meta: () => axios.get(crm_urls.crmMeta).then(unwrap),

  pipeline: () => axios.get(crm_urls.crmPipeline).then(unwrap),

  listVisitRoutes: params => axios.get(crm_urls.crmVisitRoutes, { params }).then(unwrap),
  visitRouteAssignees: () => axios.get(crm_urls.crmVisitRouteAssignees).then(unwrap),
  generateVisitRoute: body => axios.post(crm_urls.crmVisitRoutes, body).then(unwrap),
  getVisitRoute: id => axios.get(crm_urls.crmVisitRoute(id)).then(unwrap),
  editVisitRoute: (id, body) => axios.patch(crm_urls.crmVisitRoute(id), body).then(unwrap),
  logRouteContact: (id, stopId, body) => axios.post(crm_urls.crmVisitRouteContact(id, stopId), body).then(unwrap),

  listCompanies: params => axios.get(crm_urls.crmCompanies, { params }).then(unwrap),
  createCompany: body => axios.post(crm_urls.crmCompanies, body).then(unwrap),
  companyBoard: params => axios.get(crm_urls.crmCompanyBoard, { params }).then(unwrap),
  getCompany: id => axios.get(crm_urls.crmCompany(id)).then(unwrap),
  updateCompany: (id, body) => axios.patch(crm_urls.crmCompany(id), body).then(unwrap),
  setStage: (id, body) => axios.post(crm_urls.crmCompanyStage(id), body).then(unwrap),
  markLost: (id, body) => axios.post(crm_urls.crmCompanyLost(id), body).then(unwrap),
  recompute: id => axios.post(crm_urls.crmCompanyRecompute(id), {}).then(unwrap),
  addActivity: (id, body) => axios.post(crm_urls.crmCompanyActivities(id), body).then(unwrap),

  listContacts: params => axios.get(crm_urls.crmContacts, { params }).then(unwrap),
  createContact: body => axios.post(crm_urls.crmContacts, body).then(unwrap),
  updateContact: (id, body) => axios.patch(crm_urls.crmContact(id), body).then(unwrap),
  deleteContact: id => axios.delete(crm_urls.crmContact(id)).then(unwrap),
  sendContactEmail: (id, body) => axios.post(crm_urls.crmContactEmail(id), body).then(unwrap),
  previewContactEmail: (id, body) => axios.post(crm_urls.crmContactEmailPreview(id), body).then(unwrap),

  listNotifications: () => axios.get(crm_urls.crmNotifications).then(unwrap),
  markNotificationRead: id => axios.post(crm_urls.crmNotificationRead(id), {}).then(unwrap),
  markAllNotificationsRead: () => axios.post(crm_urls.crmNotificationsReadAll, {}).then(unwrap),

  prospectingMeta: () => axios.get(crm_urls.crmProspectingMeta).then(unwrap),
  runProspecting: body => axios.post(crm_urls.crmProspectingSearch, body).then(unwrap),
  listProspectSearches: () => axios.get(crm_urls.crmProspectingSearches).then(unwrap),
  prospectSearchResults: id => axios.get(crm_urls.crmProspectingSearchResults(id)).then(unwrap),
  addProspects: prospectIds => axios.post(crm_urls.crmProspectingAdd, { prospect_ids: prospectIds }).then(unwrap),

  listConversations: (status, assigned, channel) => axios.get(crm_urls.crmChatConversations, { params: { status, assigned, channel } }).then(unwrap),
  getConversation: id => axios.get(crm_urls.crmChatConversation(id)).then(unwrap),
  sendChatMessage: (id, text) => axios.post(crm_urls.crmChatSend(id), { text }).then(unwrap),
  assignConversation: (id, assignee) => axios.post(crm_urls.crmChatAssign(id), { assignee }).then(unwrap),
  closeConversation: id => axios.post(crm_urls.crmChatClose(id), {}).then(unwrap),
  reopenConversation: id => axios.post(crm_urls.crmChatReopen(id), {}).then(unwrap),
  chatUnread: (channel) => axios.get(crm_urls.crmChatUnread, { params: { channel } }).then(unwrap),

  listVisitors: params => axios.get(crm_urls.crmVisitors, { params: typeof params === 'string' ? { filter: params } : params }).then(unwrap),
  hotVisitorCount: () => axios.get(crm_urls.crmVisitorsHotCount).then(unwrap),
  markVisitorsReviewed: () => axios.post(crm_urls.crmVisitorsMarkReviewed, {}).then(unwrap),
  getVisitor: id => axios.get(crm_urls.crmVisitor(id)).then(unwrap),
  addVisitorLead: (id, body) => axios.post(crm_urls.crmVisitorAddLead(id), body || {}).then(unwrap),
  ignoreVisitor: id => axios.post(crm_urls.crmVisitorIgnore(id), {}).then(unwrap),

  callList: params => axios.get(crm_urls.crmCallList, { params }).then(unwrap),
  listTasks: params => axios.get(crm_urls.crmTasks, { params }).then(unwrap),
  createTask: body => axios.post(crm_urls.crmTasks, body).then(unwrap),
  updateTask: (id, body) => axios.patch(crm_urls.crmTask(id), body).then(unwrap),
  logCallOutcome: (id, body) => axios.post(crm_urls.crmCompanyCallOutcome(id), body).then(unwrap),

  listAbandonedQuotes: params => axios.get(crm_urls.crmAbandonedQuotes, { params }).then(unwrap),
  detectAbandonedQuotes: () => axios.post(crm_urls.crmAbandonedQuotesDetect, {}).then(unwrap),
  updateAbandonedQuote: (id, body) => axios.patch(crm_urls.crmAbandonedQuote(id), body).then(unwrap),
  sendAbandonedOutreach: (id, body) => axios.post(crm_urls.crmAbandonedQuoteOutreach(id), body).then(unwrap),
  getAbandonedOutreachPreview: id => axios.get(crm_urls.crmAbandonedQuoteOutreachPreview(id)).then(unwrap),
  getAbandonedQuoteDetail: id => axios.get(crm_urls.crmAbandonedQuoteDetail(id)).then(unwrap),
  reports: params => axios.get(crm_urls.crmReports, { params }).then(unwrap),
  subscriptionMetrics: () => axios.get(entitlementsMetrics).then(unwrap),

  aiDraft: body => axios.post(crm_urls.crmAiDraft, body).then(unwrap),
  aiMetrics: () => axios.get(crm_urls.crmAiMetrics).then(unwrap),

  pipelineBoard: pipeline => axios.get(crm_urls.crmPipelineBoard(pipeline)).then(unwrap),
  createPipelineRecord: (pipeline, body) => axios.post(crm_urls.crmPipelineRecords(pipeline), body).then(unwrap),
  updatePipelineRecord: (pipeline, id, body) => axios.patch(crm_urls.crmPipelineRecord(pipeline, id), body).then(unwrap),
  deletePipelineRecord: (pipeline, id) => axios.delete(crm_urls.crmPipelineRecord(pipeline, id)).then(unwrap),
  runSequences: () => axios.post(crm_urls.crmSequencesRun, {}).then(unwrap),

  classifyCompany: (id, body) => axios.post(crm_urls.crmCompanyClassify(id), body || {}).then(unwrap),
  runClassification: body => axios.post(crm_urls.crmClassifyRun, body || {}).then(unwrap),
  importRows: body => axios.post(crm_urls.crmImport, body).then(unwrap),

  listTemplates: () => axios.get(crm_urls.crmTemplates).then(unwrap),
  createTemplate: body => axios.post(crm_urls.crmTemplates, body).then(unwrap),
  updateTemplate: (id, body) => axios.patch(crm_urls.crmTemplate(id), body).then(unwrap),
  deleteTemplate: id => axios.delete(crm_urls.crmTemplate(id)).then(unwrap),
  previewTemplate: body => axios.post(crm_urls.crmTemplatePreview, body).then(unwrap),
  uploadAsset: file => {
    const data = new FormData();
    data.append('file', file);
    return axios.post(crm_urls.crmUploadAsset, data, { headers: { 'Content-Type': 'multipart/form-data' } }).then(unwrap);
  },

  listCampaigns: () => axios.get(crm_urls.crmCampaigns).then(unwrap),
  createCampaign: body => axios.post(crm_urls.crmCampaigns, body).then(unwrap),
  updateCampaign: (id, body) => axios.patch(crm_urls.crmCampaign(id), body).then(unwrap),
  previewCampaign: id => axios.post(crm_urls.crmCampaignPreview(id), {}).then(unwrap),
  sendCampaign: id => axios.post(crm_urls.crmCampaignSend(id), {}).then(unwrap),
  campaignStats: (id, params) => axios.get(crm_urls.crmCampaignStats(id, params)).then(unwrap),

  listUsers: () => axios.get(crm_urls.crmUsers).then(unwrap),
  addUser: body => axios.post(crm_urls.crmUsers, body).then(unwrap),
  updateUser: (id, body) => axios.patch(crm_urls.crmUser(id), body).then(unwrap),
  removeUser: id => axios.delete(crm_urls.crmUser(id)).then(unwrap),

  migrateMerchants: () => axios.post(crm_urls.crmMigrateMerchants, {}).then(unwrap),

  listDispatchAlertRecipients: () => axios.get(crm_urls.crmDispatchAlertRecipients).then(unwrap),
  createDispatchAlertRecipient: body => axios.post(crm_urls.crmDispatchAlertRecipients, body).then(unwrap),
  updateDispatchAlertRecipient: (id, body) => axios.patch(crm_urls.crmDispatchAlertRecipient(id), body).then(unwrap),
  deleteDispatchAlertRecipient: id => axios.delete(crm_urls.crmDispatchAlertRecipient(id)).then(unwrap),

  socialMeta: () => axios.get(crm_urls.crmSocialMeta).then(unwrap),
  listSocialAccounts: () => axios.get(crm_urls.crmSocialAccounts).then(unwrap),
  connectSocialAccount: body => axios.post(crm_urls.crmSocialAccounts, body).then(unwrap),
  updateSocialAccount: (id, body) => axios.patch(crm_urls.crmSocialAccount(id), body).then(unwrap),
  removeSocialAccount: id => axios.delete(crm_urls.crmSocialAccount(id)).then(unwrap),
  listSocialPosts: params => axios.get(crm_urls.crmSocialPosts, { params }).then(unwrap),
  createSocialPost: body => axios.post(crm_urls.crmSocialPosts, body).then(unwrap),
  updateSocialPost: (id, body) => axios.patch(crm_urls.crmSocialPost(id), body).then(unwrap),
  deleteSocialPost: id => axios.delete(crm_urls.crmSocialPost(id)).then(unwrap),
  scheduleSocialPost: (id, body) => axios.post(crm_urls.crmSocialPostSchedule(id), body).then(unwrap),
  publishSocialPost: id => axios.post(crm_urls.crmSocialPostPublish(id), {}).then(unwrap),
  socialAnalytics: params => axios.get(crm_urls.crmSocialAnalytics, { params }).then(unwrap),
  startFacebookConnect: () => axios.get(crm_urls.crmSocialFacebookOAuthStart).then(unwrap),
  connectFacebookToken: token => axios.post(crm_urls.crmSocialFacebookTokenConnect, { token }).then(unwrap),
  generateSocialContent: body => axios.post(crm_urls.crmSocialGenerateContent, body).then(unwrap),
  generateSocialImage: body => axios.post(crm_urls.crmSocialGenerateImage, body).then(unwrap),
  generateSocialStoryboard: body => axios.post(crm_urls.crmSocialGenerateStoryboard, body).then(unwrap),
  generateSocialVideo: body => axios.post(crm_urls.crmSocialGenerateVideo, body).then(unwrap),
  pollSocialVideo: (jobId, postId) => axios.get(crm_urls.crmSocialGenerateVideoStatus(jobId), { params: postId ? { post_id: postId } : {} }).then(unwrap),
  runSocialAutogen: body => axios.post(crm_urls.crmSocialAutogen, body || {}).then(unwrap),

  // SEO / Content
  seoMeta: () => axios.get(crm_urls.crmSeoMeta).then(unwrap),
  listSeoArticles: params => axios.get(crm_urls.crmSeoArticles, { params }).then(unwrap),
  getSeoArticle: id => axios.get(crm_urls.crmSeoArticle(id)).then(unwrap),
  updateSeoArticle: (id, body) => axios.patch(crm_urls.crmSeoArticle(id), body).then(unwrap),
  deleteSeoArticle: id => axios.delete(crm_urls.crmSeoArticle(id)).then(unwrap),
  generateSeoArticle: body => axios.post(crm_urls.crmSeoGenerate, body).then(unwrap),
  suggestSeoKeywords: body => axios.post(crm_urls.crmSeoKeywordsSuggest, body || {}).then(unwrap),
  approveSeoArticle: id => axios.post(crm_urls.crmSeoArticleApprove(id), {}).then(unwrap),
  publishSeoArticle: id => axios.post(crm_urls.crmSeoArticlePublish(id), {}).then(unwrap),
  generateSeoArticleImage: (id, body) => axios.post(crm_urls.crmSeoArticleImage(id), body || {}).then(unwrap)
};

export default crmClient;
